/**
 * SignalShare (PULSE-231) — публичная карточка сигнала.
 * /share/signal/:token, без auth: момент сигнала на графиках
 * (цена/OI/CVD/ликвидации), метрики детектора, позиции системы по
 * сигналу (если были). Кнопка «Скачать PNG» для телеграма.
 */
import { useRef } from 'react'
import { useParams } from 'react-router-dom'

import { MarketChart, type TradeMarkers } from '@/components/market/MarketChart'
import { useSharedSignal } from '@/hooks/useDashboardApi'
import { cn } from '@/lib/cn'
import { Mark, PngButton, ShareFrame, Stat } from './TradeShare'

const fmt = (v: number | null | undefined, d = 2) =>
  v == null ? '—' : `${v > 0 ? '+' : ''}${Number(v).toFixed(d).replace('-', '−')}`

export function SignalShare() {
  const { token } = useParams<{ token: string }>()
  const { data, isLoading, isError } = useSharedSignal(token)
  const cardRef = useRef<HTMLDivElement | null>(null)

  if (isLoading) {
    return <ShareFrame><div className="py-16 text-center text-textFaint text-sm">Загрузка сигнала…</div></ShareFrame>
  }
  if (isError || !data) {
    return <ShareFrame><div className="py-16 text-center text-rose text-sm">Сигнал не найден или ссылка устарела.</div></ShareFrame>
  }

  const sig = data.signal
  const coin = String(sig.symbol ?? '').replace('USDT', '')
  const isShort = sig.direction === 'short'
  const closed = data.positions.filter((p) => !p.is_open && p.realized_pnl_pct != null)
  const totalPnl = closed.length
    ? closed.reduce((a, p) => a + (p.realized_pnl_pct ?? 0), 0) / closed.length
    : null
  // на графике — первая закрытая позиция (или сигнал соло)
  const pos = closed[0] ?? data.positions[0] ?? null
  const markers: TradeMarkers = {
    direction: (pos?.direction ?? sig.direction ?? 'long') as string,
    signal: data.signal_marker,
    entry: pos?.entry_marker ?? null,
    exit: pos?.exit_marker ?? null,
    tp: pos?.take_profit_price ?? null,
    sl: pos?.stop_loss_price ?? null,
  }

  return (
    <ShareFrame>
      <div className="flex items-center justify-end mb-2">
        <PngButton node={cardRef} filename={`pulsar-signal-${coin}`} />
      </div>

      <div ref={cardRef}
        className="bg-surface border border-line rounded-lg shadow-soft overflow-hidden flex flex-col"
        style={{ height: 'calc(100vh - 76px)', minHeight: 560 }}>

        {/* верх: бренд + сигнал */}
        <div className="flex items-center gap-3 flex-wrap px-4 py-3 border-b border-line">
          <Mark />
          <span className="w-px h-4 bg-line" />
          <span className="text-lg font-semibold">
            {coin}<span className="text-textFaint text-xs font-normal">USDT</span>
          </span>
          <span className={cn('pill font-bold',
            isShort ? 'bg-rose-faint text-rose' : 'bg-pulse-faint text-pulse')}>
            {(sig.direction ?? '—').toUpperCase()}
          </span>
          <span className="pill bg-fire-faint text-fire">{sig.signal_type}</span>
          {sig.timeframe && <span className="pill bg-elevated text-textDim">{sig.timeframe}</span>}
          <span className="ml-auto text-2xs text-textFaint font-mono">
            {new Date(sig.ts).toLocaleString('ru-RU', {
              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
            })}
          </span>
        </div>

        {/* метрики детектора + итог позиций */}
        <div className="flex items-center gap-5 flex-wrap px-4 py-3 border-b border-line">
          <div className="flex items-baseline gap-3">
            <span className={cn('font-mono text-3xl font-semibold leading-none tracking-tight',
              totalPnl == null ? 'text-text' : totalPnl >= 0 ? 'text-pulse' : 'text-rose')}>
              {totalPnl != null ? `${fmt(totalPnl)}%` : `${fmt(sig.dp_pct)}%`}
            </span>
            <span className="pill bg-elevated text-textDim">
              {totalPnl != null
                ? `результат системы · ${closed.length} ${closed.length === 1 ? 'сделка' : 'сделки'}`
                : 'импульс цены на сигнале'}
            </span>
          </div>
          <div className="ml-auto flex items-baseline gap-5 flex-wrap">
            <Stat k="Δцена" v={`${fmt(sig.dp_pct)}%`} />
            <Stat k="ΔOI" v={`${fmt(sig.doi_pct)}%`} />
            <Stat k="score" v={fmt(sig.score, 1)} />
            {sig.rvol != null && <Stat k="rvol" v={fmt(sig.rvol, 1)} />}
            <Stat k="цена" v={sig.price != null ? String(sig.price) : '—'} />
          </div>
        </div>

        {/* графики */}
        <div className="flex-1 min-h-0 p-1.5">
          <MarketChart data={data.series} markers={markers} />
        </div>

        <div className="px-4 py-2 border-t border-line text-2xs text-textFaint flex items-center">
          <span>момент сигнала размечен на каждом графике</span>
          <span className="ml-auto font-mono">pulsar.paradigmadev.com</span>
        </div>
      </div>
    </ShareFrame>
  )
}
